/**
 * Every object in the `uploads` bucket lives at
 * `{repositoryId}/{timestamp}-{filename}-{uuid}.{extension}`.
 *
 * `/api/files` signs any path of this shape for anyone who asks, because GitHub
 * and other apps load these links without a cubic session. The random UUID in
 * the name is the only thing keeping a file private, so the route refuses every
 * other shape and nothing is allowed to list the bucket.
 */
const UPLOAD_FILE_PATH_PATTERN = /^\d+\/\d+-[\w.-]+$/;

/**
 * Checks whether a path has the shape of a file uploaded through `generateUploadFilePath`.
 *
 * @param path - The object path inside the uploads bucket
 * @returns True when the path is `{repositoryId}/{timestamp}-{name}` with URL-safe characters only
 */
export function isUploadFilePath(path: string): boolean {
  return UPLOAD_FILE_PATH_PATTERN.test(path);
}

/**
 * Sanitizes a filename to be safe for URL use
 * Removes or replaces characters that could cause issues.
 *
 * @param filename - The original filename to sanitize
 * @returns A sanitized version of the filename
 */
const sanitizeFilename = (filename: string): string => {
  return (
    filename
      // Replace spaces with dashes
      .replace(/\s+/g, '-')
      // Replace any non-alphanumeric characters except dashes and dots
      .replace(/[^\w\-.]/g, '-')
      // Replace multiple consecutive dashes with a single dash
      .replace(/-+/g, '-')
      // Remove leading/trailing dashes
      .replace(/^-|-$/g, '') ||
    // Ensure the filename isn't empty after sanitization
    'unnamed-file'
  );
};

/**
 * Generates a unique file path for uploading a file to Supabase Storage.
 *
 * This function:
 * - Creates a path with the repository ID as a folder
 * - Generates a unique filename using timestamp and a random UUID to prevent conflicts and make it unguessable
 * - Preserves the original file extension, sanitized so the path stays URL-safe
 *
 * @param repoId - The ID of the repository to use as the parent folder
 * @param file - The file object containing name and other metadata
 * @returns A unique path string in the format "{repoId}/{timestamp}-{filename}-{uuid}.{extension}"
 * @throws Error if repository ID or file is invalid
 */
export function generateUploadFilePath(repoId: number, file: File): string {
  if (!repoId || !file) {
    throw new Error('Invalid repository ID or file name');
  }

  const fileName = sanitizeFilename(file.name.split('.').slice(0, -1).join('.'));
  const ext = sanitizeFilename(file.name.split('.').pop() ?? '');
  const uniqueName = `${Date.now()}-${fileName}-${crypto.randomUUID()}.${ext}`;
  return `${repoId}/${uniqueName}`;
}
